import { useState, useEffect, useCallback, useMemo } from 'react'
import { PageHeader, colors, btnPrimary } from '../../components/ui'
import { getAuthToken } from '../../services/api'

interface InstanceOption {
  value: string
  label: string
  default?: boolean
  [key: string]: unknown
}

interface ProviderEntry {
  id: string
  label: string
  instanceTypes: InstanceOption[]
  [key: string]: unknown
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  return { Authorization: `Bearer ${getAuthToken()}`, ...extra }
}

const smallBtnStyle = {
  padding: '0.4rem 0.75rem',
  borderRadius: '4px',
  border: '1px solid #d1d5db',
  background: '#fff',
  cursor: 'pointer',
  fontSize: '0.85rem',
}

function tryParseProviders(raw: string): ProviderEntry[] | null {
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return parsed as ProviderEntry[]
  } catch {
    return null
  }
}

export function ProvisioningConfigPage() {
  const [json, setJson] = useState('')
  const [savedJson, setSavedJson] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/admin/settings/provisioning-providers', {
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error(`Failed to load (${res.status})`)
      const body = (await res.json()) as { data: unknown[] }
      const formatted = JSON.stringify(body.data, null, 2)
      setJson(formatted)
      setSavedJson(formatted)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load config')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  const handleJsonChange = (value: string) => {
    setJson(value)
    setSuccess(null)
    try {
      JSON.parse(value)
      setParseError(null)
    } catch (e) {
      setParseError(e instanceof Error ? e.message : 'Invalid JSON')
    }
  }

  const providers = useMemo(() => tryParseProviders(json), [json])

  const handleDefaultChange = (providerId: string, instanceValue: string) => {
    if (!providers) return
    const updated = providers.map((p) => {
      if (p.id !== providerId) return p
      return {
        ...p,
        instanceTypes: p.instanceTypes.map((it) => {
          const copy = { ...it }
          if (it.value === instanceValue) {
            copy.default = true
          } else {
            delete copy.default
          }
          return copy
        }),
      }
    })
    const formatted = JSON.stringify(updated, null, 2)
    setJson(formatted)
    setParseError(null)
    setSuccess(null)
  }

  const handleSave = async () => {
    let parsed: unknown
    try {
      parsed = JSON.parse(json)
    } catch {
      setParseError('Invalid JSON — fix syntax errors before saving')
      return
    }
    if (!Array.isArray(parsed)) {
      setParseError('Top-level value must be an array of providers')
      return
    }
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await fetch('/api/admin/settings/provisioning-providers', {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ providers: parsed }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `Save failed (${res.status})`)
      }
      const body = (await res.json()) as { data: unknown[] }
      const formatted = JSON.stringify(body.data, null, 2)
      setJson(formatted)
      setSavedJson(formatted)
      setSuccess('Saved')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await fetch('/api/admin/settings/provisioning-providers/reset', {
        method: 'POST',
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error(`Reset failed (${res.status})`)
      const body = (await res.json()) as { data: unknown[] }
      const formatted = JSON.stringify(body.data, null, 2)
      setJson(formatted)
      setSavedJson(formatted)
      setParseError(null)
      setSuccess('Reset to defaults')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reset failed')
    } finally {
      setSaving(false)
    }
  }

  const dirty = json !== savedJson

  if (loading) return <PageHeader title="Provisioning Providers" subtitle="Loading..." />

  return (
    <div>
      <PageHeader
        title="Provisioning Providers"
        subtitle="Edit provider configuration as JSON. Extra fields are preserved for provider-specific API config."
      />

      {error && (
        <div
          style={{
            padding: '0.75rem',
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            color: '#dc2626',
            marginBottom: '1rem',
            fontSize: '0.875rem',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: '0.75rem',
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: '6px',
            color: '#16a34a',
            marginBottom: '1rem',
            fontSize: '0.875rem',
          }}
        >
          {success}
        </div>
      )}

      {providers && providers.length > 0 && (
        <div
          style={{
            padding: '1rem',
            background: colors.bgCard,
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            marginBottom: '1rem',
          }}
        >
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>
            Recommended Instance Type
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {providers.map((p) => (
              <div key={p.id}>
                <div style={{ fontSize: '0.8rem', fontWeight: 500, color: '#6b7280', marginBottom: '0.25rem' }}>
                  {p.label || p.id}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {(p.instanceTypes ?? []).map((it) => (
                    <label
                      key={it.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        fontSize: '0.825rem',
                        cursor: 'pointer',
                        padding: '0.3rem 0.6rem',
                        borderRadius: '4px',
                        border: `1px solid ${it.default ? '#3b82f6' : '#e5e7eb'}`,
                        background: it.default ? '#eff6ff' : '#fff',
                      }}
                    >
                      <input
                        type="radio"
                        name={`default-${p.id}`}
                        checked={!!it.default}
                        onChange={() => handleDefaultChange(p.id, it.value)}
                        style={{ margin: 0 }}
                      />
                      {it.label || it.value}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          padding: '1.25rem',
          background: colors.bgCard,
          borderRadius: '8px',
          border: `1px solid ${parseError ? '#fca5a5' : colors.border}`,
          marginBottom: '0.5rem',
        }}
      >
        <textarea
          value={json}
          onChange={(e) => handleJsonChange(e.target.value)}
          spellCheck={false}
          style={{
            width: '100%',
            minHeight: '500px',
            fontFamily: 'ui-monospace, "Cascadia Code", "Source Code Pro", Menlo, monospace',
            fontSize: '0.825rem',
            lineHeight: 1.5,
            padding: '0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            background: '#fafafa',
            color: '#1f2937',
            resize: 'vertical',
            boxSizing: 'border-box',
            tabSize: 2,
          }}
        />
      </div>

      {parseError && <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{parseError}</div>}

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
        <button style={{ ...smallBtnStyle, color: '#6b7280' }} onClick={handleReset} disabled={saving}>
          Reset to Defaults
        </button>
        <div style={{ flex: 1 }} />
        <button
          style={{ ...btnPrimary, opacity: !dirty || !!parseError || saving ? 0.5 : 1 }}
          onClick={handleSave}
          disabled={!dirty || !!parseError || saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
